import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import Icon from '@mdi/react';
import { mdiArrowLeft, mdiArrowRight, mdiFilter } from '@mdi/js';
import CampoTexto from '../../components/Formulario/CampoTexto';
import Grid, { GridItem } from '../../components/layout/Grid';
import CampoRadio from '../../components/Formulario/CampoRadio';
import Subtitulo from '../../components/tipografia/Subtitulo';
import CampoSeletor from '../../components/Formulario/CampoSeletor';
import CampoData from '../../components/Formulario/CampoData';
import Botao from '../../components/Botao';
import AlertBox from '../../components/layout/AlertBox';
import VBox from '../../components/layout/VBox';
import axios from 'axios';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import CampoArquivo from '@/components/Formulario/CampoArquivo';
import HBox from '@/components/layout/HBox';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import { nomesParametros } from '@tera/shared/src/lib/modeloMensagem';

type CanalOptions = {
  label: string;
  value: string;
  placeholder?: string;
  elem: ({ value, onChange }: any) => React.ReactElement;
};

type Props = {
  onCompleted?: () => void;
  onError?: () => void;
  hideControls?: boolean;
};

export const FormCampanha = ({ onCompleted, onError, hideControls }: Props) => {
  const [step, setStep] = useState(0);
  const [filter, setFilter] = useState<any>(null);
  const [canGoNextStep, setCanGoNextStep] = useState(false);
  const [canGoPreviousStep, setCanGoPreviousStep] = useState(true);

  const queryCanais = useQuery(
    ['canal'],
    async () => {
      const res = await fetch('/api/canal/box');
      return res.json();
    },
    {
      enabled: step === 1,
    },
  );

  const queryMsgProgramadas = useQuery(
    ['mensagem_programada'],
    async () => {
      const res = await fetch('/api/mensagem_programada/box');
      return res.json();
    },
    {
      enabled: step === 2,
    },
  );

  const msgPrgOptions =
    queryMsgProgramadas.data?.result
      ?.filter((it: any) => !it.botoes || it.botoes.length === 0)
      .map((it: any) => ({
        id: it.id,
        label: it.nome,
      })) ?? [];

  const [origem, setOrigem] = useState('cliente_grupo');
  const [msgProgramadaId, setMsgProgramadaId] = useState<string | null>(null);
  const [campoCanalClienteId, setCampoCanalClienteId] = useState<string | null>(
    null,
  );
  const [associacoes, setAssociacoes] = useState<
    Record<string, { tipo: 'fixo' | 'campo'; valor: string }>
  >({});
  const [arquivo, setArquivo] = useState<[string, string]>(); // [nome, conteudo]
  const [analiseArquivo, setAnaliseArquivo] = useState({
    campos: [],
    amostras: [],
    qtdContatos: 0,
  });

  const [camposOptions, setCamposOptions] = useState<
    { id: string; label: string }[]
  >([]);

  const msgProgramada = queryMsgProgramadas.data?.result?.find(
    (msg: any) => msg.id?.toString() == msgProgramadaId?.toString(),
  );
  // nome repetido no texto é um parâmetro só
  const msgProgramadaParams = nomesParametros(msgProgramada?.conteudo);
  //const msgProgramadaParams = msgProgramada?.conteudo?.replace(/{{[^}]+}}/g, (match) => match.replace(/[{}]/g, ''))?.split(',') ?? []

  const qClienteGrupos = useQuery(
    'cliente_grupo/box',
    async () => {
      const res = await axios.get('/api/cliente_grupo/box');
      return res.data;
    },
    {
      enabled: origem === 'cliente_grupo',
    },
  );
  const gruposOptions =
    qClienteGrupos.data?.result?.map((it: any) => ({
      id: it.id,
      label: `${it.nome} (${it.total_clientes} contatos)`,
    })) ?? [];

  const qClientes = useQuery(
    'cliente/box',
    async () => {
      const res = await axios.get('/api/cliente/box');
      return res.data;
    },
    {
      enabled: origem === 'cliente',
    },
  );
  const clientesOptions =
    qClientes.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) ?? [];

  const qCustomize = useQuery('cliente/meta', async () => {
    const res = await axios.get('/api/cliente/meta');
    return res.data.result;
  });

  const analiseMutator = useMutation(
    (data: any) => {
      const fd = new FormData();
      fd.append('file', data.file, data.file.name);

      return fetch('/api/campanha_mensagem/analise', {
        method: 'POST',
        body: fd,
      }).then(res => res.json());
    },
    {
      onSuccess: (data, vars) => {
        setArquivo([vars.file.name, vars.file]);
        setAnaliseArquivo(data);
      },
      onSettled: () => {
        console.log('onSettled');
        analiseMutator.reset();
      },
    },
  );

  const executeMutator = useMutation(
    (data: any) => {
      const fd = new FormData();
      fd.append('associacoes', JSON.stringify(data.associacoes));
      fd.append('tipo_disparo', data.tipo_disparo ?? 'simples');
      if (data.tipo_disparo === 'avancado' && data.dialogo_disparo_id != null)
        fd.append('dialogo_disparo_id', data.dialogo_disparo_id);
      if (data.tipo_disparo !== 'avancado')
        fd.append('mensagem_programada_id', data.mensagem_programada_id);
      fd.append('campo_canal_cliente_id', data.campo_canal_cliente_id ?? '');
      if (data.grupo_encaminhamento_id != null && !Number.isNaN(data.grupo_encaminhamento_id))
        fd.append('grupo_encaminhamento_id', data.grupo_encaminhamento_id);
      if (data.dialogo_encaminhamento_id != null && !Number.isNaN(data.dialogo_encaminhamento_id))
        fd.append('dialogo_encaminhamento_id', data.dialogo_encaminhamento_id);
      fd.append('origem', data.origem);
      if (data.data_agendamento)
        fd.append('data_agendamento', data.data_agendamento);

      data.grupo_ids?.forEach((id: string) => fd.append('grupo_ids[]', id));
      data.cliente_ids?.forEach((id: string) => fd.append('cliente_ids[]', id));
      if (data.file) fd.append('file', data.file, data.file.name);

      return axios.post('/api/campanha_mensagem/execute', fd).then(res => res.data);
    },
    {
      onSuccess: (data, vars) => {
        onCompleted?.();
      },
      onError: (err, vars) => {
        onError?.();
      }
    },
  );

  const handleFileChange = (files: FileList | null) => {
    analiseMutator.mutate({ file: files?.[0] });
  };

  const [tipoDisparo, setTipoDisparo] = useState<'simples' | 'avancado'>('simples');
  const [dialogoDisparoId, setDialogoDisparoId] = useState<string | null>(null);

  const [tipoInicio, setTipoInicio] = useState<'imediato' | 'agendado'>('imediato');
  const [dataAgendamento, setDataAgendamento] = useState<string>('');

  const queryFluxosCampanha = useQuery(
    ['dialogo/box/campanha'],
    async () => {
      const res = await fetch('/api/dialogo/box?inicializacao=campanha');
      return res.json();
    },
    { enabled: tipoDisparo === 'avancado' && step === 2 },
  );

  const fluxosCampanhaOptions =
    queryFluxosCampanha.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) ?? [];

  const queryFluxoDisparo = useQuery(
    ['dialogo', dialogoDisparoId],
    async () => {
      const res = await fetch(`/api/dialogo/${dialogoDisparoId}`);
      return res.json();
    },
    { enabled: tipoDisparo === 'avancado' && !!dialogoDisparoId },
  );

  const variavelFluxo: { nome: string; tipo: string }[] = useMemo(() => {
    if (!queryFluxoDisparo.data?.result) return [];
    const nodes = queryFluxoDisparo.data.result.conteudo?.nodes ?? {};
    const startNode = Object.values(nodes).find((n: any) => n.root) as any;
    return startNode?.values?.variaveisCampanha ?? [];
  }, [queryFluxoDisparo.data]);

  const [grupoEncaminhamentoId, setGrupoEncaminhamentoId] = useState<string | null>(null);
  const [tipoEncaminhamento, setTipoEncaminhamento] = useState<'padrao' | 'grupo' | 'fluxo'>('padrao');
  const [dialogoEncaminhamentoId, setDialogoEncaminhamentoId] = useState<string | null>(null);

  const queryGruposEncaminhamento = useQuery(
    ['usuario_grupo'],
    async () => {
      const res = await fetch('/api/usuario_grupo/box');
      return res.json();
    },
    {
      enabled: tipoEncaminhamento === 'grupo',
    },
  );

  const gruposEncaminhamentoOptions =
    queryGruposEncaminhamento.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) ?? [];

  const queryDialogosEncaminhamento = useQuery(
    ['dialogo/box/encaminhamento'],
    async () => {
      const res = await fetch('/api/dialogo/box?inicializacao=canal&inicializacao=manual');
      return res.json();
    },
    {
      enabled: tipoEncaminhamento === 'fluxo',
    },
  );

  const dialogosEncaminhamentoOptions =
    queryDialogosEncaminhamento.data?.result?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    })) ?? [];

  const handleExecute = useCallback(async () => {
    if (executeMutator.isLoading) {
      return;
    }

    console.log('executar campanha');
    executeMutator.mutateAsync({
      associacoes,
      tipo_disparo: tipoDisparo,
      dialogo_disparo_id: tipoDisparo === 'avancado' ? dialogoDisparoId : undefined,
      mensagem_programada_id: tipoDisparo !== 'avancado' ? msgProgramadaId : undefined,
      campo_canal_cliente_id: campoCanalClienteId,
      origem,
      grupo_ids: filter?.grupo_ids,
      cliente_ids: filter?.cliente_ids,
      file: arquivo?.[1],
      grupo_encaminhamento_id: tipoDisparo === 'simples' && tipoEncaminhamento === 'grupo' ? grupoEncaminhamentoId : undefined,
      dialogo_encaminhamento_id: tipoDisparo === 'simples' && tipoEncaminhamento === 'fluxo' ? dialogoEncaminhamentoId : undefined,
      data_agendamento: tipoInicio === 'agendado' && dataAgendamento ? new Date(dataAgendamento).toISOString() : undefined,
    });
    setStep(4);
  }, [tipoEncaminhamento, grupoEncaminhamentoId, dialogoEncaminhamentoId, msgProgramadaId, campoCanalClienteId, origem, filter, arquivo, associacoes, tipoInicio, dataAgendamento]);

  useEffect(() => {
    if (step == 0) {
      setCanGoNextStep(true);
    } else if (step == 1) {
      setCanGoNextStep(
        (origem === 'cliente' && filter?.cliente_ids?.length > 0) ||
        (origem === 'cliente_grupo' && filter?.grupo_ids?.length > 0) ||
        (origem === 'upload' && analiseArquivo?.qtdContatos > 0),
      );
    } else if (step == 2) {
      if (tipoDisparo === 'avancado') {
        setCanGoNextStep(
          dialogoDisparoId != null &&
          dialogoDisparoId.toString().length > 0 &&
          (variavelFluxo.length === 0 ||
            (associacoes &&
              variavelFluxo.every(v => associacoes[v.nome] != null && associacoes[v.nome].valor.length > 0))) &&
          ((origem === 'upload' &&
            campoCanalClienteId != null &&
            campoCanalClienteId.toString().length > 0) ||
            origem !== 'upload'),
        );
      } else {
        setCanGoNextStep(
          msgProgramadaId != null &&
          msgProgramadaId.toString().length > 0 &&
          (msgProgramadaParams.length === 0 ||
            (associacoes &&
              Object.values(associacoes).length > 0 &&
              Object.values(associacoes).every(
                it => it != null && it.valor.length > 0,
              ))) &&
          ((origem === 'upload' &&
            campoCanalClienteId != null &&
            campoCanalClienteId.toString().length > 0) ||
            origem !== 'upload'),
        );
      }
    } else if (step == 3) {
      setCanGoNextStep(
        tipoInicio === 'imediato' ||
        (!!dataAgendamento && new Date(dataAgendamento) > new Date()),
      );
    }
  }, [
    step,
    analiseArquivo?.qtdContatos,
    msgProgramadaId,
    campoCanalClienteId,
    origem,
    filter,
    associacoes,
    msgProgramadaParams,
    tipoDisparo,
    dialogoDisparoId,
    variavelFluxo,
    tipoInicio,
    dataAgendamento,
  ]);

  useEffect(() => {
    if (origem === 'upload')
      setCamposOptions(
        analiseArquivo?.campos?.map(campo => ({
          id: campo,
          label: campo,
        })),
      );
    if (['cliente', 'cliente_grupo'].includes(origem))
      setCamposOptions([
        { id: 'nome', label: 'Nome do cliente' },
        // { id: 'contatos.valor', label: 'Contato' },
        ...(qCustomize.data?.map((it: any) => ({
          id: it.nome,
          label: it.rotulo,
        })) ?? []),
        { id: '[[fixo]]', label: '(Texto fixo)' },
      ]);
  }, [origem, filter, analiseArquivo, qCustomize.data]);

  const renderHeaderPreview = useCallback((mp: any) => {
    if (!mp) return null;
    if (!mp.cabecalho_tipo || mp.cabecalho_tipo === 'nenhum') return null;
    if (mp.cabecalho_tipo === 'texto') {
      return (
        <div style={{ marginBottom: '8px', color: '#103668' }}>
          <b>{mp.cabecalho_texto}</b>
        </div>
      );
    }
    if (!mp.cabecalho_texto) return null;
    const filename = mp.cabecalho_texto;
    const parts = ["/api/assets", "mensagem_programada", mp.id, encodeURIComponent(filename)].join('/')

    if (mp.cabecalho_tipo === 'imagem') {
      return (
        <div style={{ marginBottom: '8px' }}>
          <img src={parts} alt="cabeçalho" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: 8 }} />
        </div>
      );
    }
    if (mp.cabecalho_tipo === 'video') {
      return (
        <div style={{ marginBottom: '8px' }}>
          <video controls style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: 8 }}>
            <source src={parts} type="video/mp4" />
          </video>
        </div>
      );
    }
    if (mp.cabecalho_tipo === 'documento') {
      return (
        <div style={{ marginBottom: '8px' }}>
          <a href={parts} target="_blank" rel="noreferrer">{filename}</a>
        </div>
      );
    }
    return null;
  }, []);

  return (
    <>
      <Grid>
        {(['Tipo', 'Origem', 'Mensagem', 'Revisão'] as const).map((label, i) => (
          <GridItem key={label} sm={12} md={3}>
            <div
              style={{
                padding: '1rem 2rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderBottom: step >= i ? '2px solid #8CFF8C' : '2px solid #103668',
                color: step >= i ? '#333' : '#CCC',
              }}
            >
              {i + 1}. {label}
            </div>
          </GridItem>
        ))}
      </Grid>

      {step === 0 && (
        <HBox gap="24px" style={{ justifyContent: 'center', padding: '32px 0' }}>
          {([
            {
              id: 'simples' as const,
              titulo: 'Rápido',
              subtitulo: 'Modelos multimídia',
              descricao: 'Envie uma mensagem a partir de um modelo aprovado',
            },
            {
              id: 'avancado' as const,
              titulo: 'Avançado',
              subtitulo: 'Fluxos interativos',
              descricao: 'Fluxos de campanha personalizados',
            },
          ] as const).map(({ id, titulo, subtitulo, descricao }) => (
            <div
              key={id}
              onClick={() => {
                setTipoDisparo(id);
                if (id === 'simples') setDialogoDisparoId(null);
                else setMsgProgramadaId(null);
                setStep(1);
              }}
              style={{
                cursor: 'pointer',
                flex: 1,
                maxWidth: '280px',
                padding: '32px 24px',
                border: `2px solid ${tipoDisparo === id ? 'var(--tc-color-primary)' : 'var(--tc-color-gray-400)'}`,
                borderRadius: '12px',
                backgroundColor: tipoDisparo === id ? 'var(--tc-color-primary-light)' : 'var(--tc-color-white)',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: tipoDisparo === id ? '0 2px 12px var(--tc-color-primary-light)' : '0 1px 4px #0001',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--tc-color-primary)' }}>{titulo}</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--tc-color-gray-800)' }}>{subtitulo}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--tc-color-gray-600)', marginTop: '4px' }}>{descricao}</div>
            </div>
          ))}
        </HBox>
      )}

      {step === 1 && (
        <Grid>
          <GridItem sm={12}>
            <VBox>
              <label>De onde virão os dados?</label>
              <CampoRadio
                id="origem-1"
                name="origem"
                label="De um grupo de clientes cadastrados na Terachat"
                checked={origem == 'cliente_grupo'}
                onChange={() => setOrigem('cliente_grupo')}
              />
              <CampoRadio
                id="origem-3"
                name="origem"
                label="De um arquivo que farei upload"
                checked={origem == 'upload'}
                onChange={() => setOrigem('upload')}
              />
              <CampoRadio
                id="origem-2"
                name="origem"
                label="De clientes específicos, cadastrados na Terachat"
                checked={origem == 'cliente'}
                onChange={() => setOrigem('cliente')}
              />
            </VBox>
          </GridItem>

          {origem == 'cliente_grupo' && (
            <GridItem sm={12}>
              <VBox>
                <Subtitulo>Grupos de clientes</Subtitulo>
                <CampoMultiSeletor
                  instanceId="grupo_ids"
                  value={filter?.grupo_ids ?? ''}
                  onChange={v => setFilter({ ...filter, grupo_ids: v })}
                  options={gruposOptions}
                  placeholder="Selecione..."
                />
              </VBox>
            </GridItem>
          )}

          {origem == 'cliente' && (
            <GridItem sm={12}>
              <VBox>
                <Subtitulo>Clientes</Subtitulo>
                <CampoMultiSeletor
                  instanceId="cliente_ids"
                  value={filter?.cliente_ids ?? ''}
                  onChange={v => setFilter({ ...filter, cliente_ids: v })}
                  options={clientesOptions}
                  placeholder="Selecione..."
                />
              </VBox>
            </GridItem>
          )}

          {origem == 'upload' && (
            <GridItem sm={12}>
              <VBox>
                <Subtitulo>Upload de arquivo</Subtitulo>

                <CampoArquivo
                  instanceId='upload_file'
                  onChange={handleFileChange}
                  disabled={analiseMutator.isLoading}
                  accept=".csv"
                  style={{ width: '100%' }}
                />

                {analiseMutator.isLoading && (
                  <div>Aguarde. Validando arquivo...</div>
                )}

                {!analiseMutator.isLoading && analiseArquivo && (
                  <div>
                    <div>
                      Contatos encontrados: {analiseArquivo.qtdContatos}
                    </div>
                  </div>
                )}

                <AlertBox variant="info" style={{ width: '550px' }}>
                  <p>
                    Informe um arquivo CSV com os contatos e as informações
                    relevantes para preencher a mensagem a ser enviada.
                  </p>
                  <p>
                    O arquivo CSV deve ter, no mínimo, o contato; Por exemplo, o
                    número de telefone.
                  </p>
                </AlertBox>
              </VBox>
            </GridItem>
          )}
        </Grid>
      )}

      {step === 2 && tipoDisparo === 'avancado' && (
        <Grid>
          <GridItem sm={12}>
            <VBox>
              <label>Qual fluxo de campanha será utilizado?</label>
              <CampoSeletor
                instanceId='dialogo_disparo_id'
                value={dialogoDisparoId ?? undefined}
                onChange={(v: any) => { setDialogoDisparoId(v); setAssociacoes({}); }}
                options={fluxosCampanhaOptions}
                placeholder="Selecione um fluxo de campanha"
              />

              {dialogoDisparoId && variavelFluxo.length > 0 && (
                <div>
                  <p>Este fluxo declara as seguintes variáveis de entrada. Associe cada uma a um campo do destinatário ou a um texto fixo.</p>
                  <table style={{ width: '100%' }}>
                    <tbody>
                      {origem === 'upload' && (
                        <tr>
                          <td style={{ width: '50%' }}><b>Telefone</b></td>
                          <td style={{ width: '50%' }}>
                            <CampoSeletor
                              instanceId='canal_cliente_id_av'
                              options={camposOptions}
                              value={campoCanalClienteId ?? undefined}
                              onChange={setCampoCanalClienteId}
                              placeholder="Coluna do telefone..."
                            />
                          </td>
                        </tr>
                      )}
                      {variavelFluxo.map((varItem) => (
                        <tr key={varItem.nome}>
                          <td style={{ width: '50%' }}>{varItem.nome}</td>
                          <td style={{ width: '50%' }}>
                            {(associacoes[varItem.nome]?.tipo ?? 'campo') === 'fixo' ? (
                              <HBox>
                                <div style={{ width: 'auto' }}>
                                  <CampoCheckbox
                                    title="Desmarque para associar com um campo"
                                    value={true}
                                    onChange={() => setAssociacoes({ ...associacoes, [varItem.nome]: { tipo: 'campo', valor: '' } })}
                                  />
                                </div>
                                <div style={{ width: '100%' }}>
                                  <CampoTexto
                                    value={associacoes[varItem.nome]?.valor ?? ''}
                                    onChange={(valor: string) => setAssociacoes({ ...associacoes, [varItem.nome]: { tipo: 'fixo', valor } })}
                                    placeholder="Texto fixo..."
                                  />
                                </div>
                              </HBox>
                            ) : (
                              <CampoSeletor
                                instanceId={`assoc-av-valor-${varItem.nome}`}
                                options={[
                                  ...camposOptions,
                                  ...(origem === 'upload' ? [{ id: '[[fixo]]', label: '(Texto fixo)' }] : []),
                                ]}
                                value={associacoes[varItem.nome]?.valor ?? undefined}
                                onChange={(valor: any) => {
                                  if (valor === '[[fixo]]') {
                                    setAssociacoes({ ...associacoes, [varItem.nome]: { tipo: 'fixo', valor: '' } });
                                  } else {
                                    setAssociacoes({ ...associacoes, [varItem.nome]: { tipo: 'campo', valor } });
                                  }
                                }}
                                placeholder="Selecione o campo..."
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {dialogoDisparoId && variavelFluxo.length === 0 && !queryFluxoDisparo.isLoading && (
                <AlertBox variant="info">
                  Este fluxo não declara variáveis de entrada. Os dados do destinatário não serão passados para o fluxo.
                </AlertBox>
              )}
            </VBox>
          </GridItem>
        </Grid>
      )}

      {step === 2 && tipoDisparo === 'simples' && (
        <Grid>
          <GridItem sm={12}>
            <VBox>
              <label>Qual mensagem será enviada?</label>
              <CampoSeletor
                instanceId='mensagem_programada_id'
                value={msgProgramadaId ?? undefined}
                onChange={setMsgProgramadaId}
                options={msgPrgOptions}
                placeholder="Selecione uma mensagem"
              />

              <div
                style={{
                  fontSize: '1.2rem',
                  color: '#103668',
                  textAlign: 'center',
                  padding: '1rem',
                  border: '1px solid #103668',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--tc-color-gray-100)',
                }}
              >
                {msgProgramadaId?.toString()?.length ? (
                  <div>
                    {renderHeaderPreview(msgProgramada)}
                    <div>{msgProgramada?.conteudo}</div>
                  </div>
                ) : (
                  <span style={{ color: 'gray', fontStyle: 'italic' }}>
                    Nenhuma mensagem selecionada.
                  </span>
                )}
              </div>
              <hr />

              {msgProgramadaId?.toString()?.length ? (
                msgProgramadaParams?.length > 0 && camposOptions.length > 0 ? (
                  <div>
                    <div>
                      <p>
                        Esta mensagem possui trechos que podem ser alterados
                        conforme os dados dos clientes.
                      </p>
                      <p>
                        Altere as associações conforme desejar para alterar a
                        mensagem final.
                      </p>
                    </div>
                    <table style={{ width: '100%' }}>
                      <tbody>
                        {origem === 'upload' && (
                          <tr>
                            <td style={{ width: '50%' }}>
                              <b>Telefone</b>
                            </td>
                            <td style={{ width: '50%' }}>
                              <CampoSeletor
                                instanceId='canal_cliente_id'
                                options={camposOptions}
                                value={campoCanalClienteId ?? ''}
                                onChange={setCampoCanalClienteId}
                              />
                            </td>
                          </tr>
                        )}
                        {msgProgramadaParams?.map((param: string, idx) => (
                          <tr key={idx}>
                            <td style={{ width: '50%' }}>
                              <b>{param}</b>
                            </td>
                            <td style={{ width: '50%' }}>
                              {associacoes[param]?.tipo === 'fixo' ? (
                                <HBox>
                                  <div style={{ width: 'auto' }}>
                                    <CampoCheckbox
                                      title="Desmarque para associar com um campo"
                                      value={
                                        associacoes[param]?.tipo === 'fixo'
                                      }
                                      onChange={v =>
                                        setAssociacoes({
                                          ...associacoes,
                                          [param]: { tipo: 'campo', valor: '' },
                                        })
                                      }
                                    />
                                  </div>
                                  <div style={{ width: '100%' }}>
                                    <CampoTexto
                                      placeholder="Digite aqui o texto..."
                                      value={associacoes[param]?.valor ?? ''}
                                      onChange={v =>
                                        setAssociacoes({
                                          ...associacoes,
                                          [param]: { tipo: 'fixo', valor: v },
                                        })
                                      }
                                    />
                                  </div>
                                </HBox>
                              ) : (
                                <CampoSeletor
                                  options={camposOptions}
                                  value={associacoes[param]?.valor ?? ''}
                                  onChange={v => {
                                    if (v === '[[fixo]]') {
                                      setAssociacoes({
                                        ...associacoes,
                                        [param]: { tipo: 'fixo', valor: '' },
                                      });
                                    } else {
                                      setAssociacoes({
                                        ...associacoes,
                                        [param]: { tipo: 'campo', valor: v as string },
                                      });
                                    }
                                  }}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div>
                    {origem === 'upload' && (
                      <>
                        <p>
                          De quais campos do seu arquivo CSV virão as
                          informações a seguir?
                        </p>
                        <table style={{ width: '100%' }}>
                          <thead>
                            <tr>
                              <th>Info. p/ notificação</th>
                              <th>Campo informação</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={{ width: '50%' }}>
                                <b>Telefone</b>
                              </td>
                              <td style={{ width: '50%' }}>
                                <CampoSeletor
                                  instanceId='canal_cliente_id'
                                  options={camposOptions}
                                  value={campoCanalClienteId ?? ''}
                                  onChange={setCampoCanalClienteId}
                                />
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </>
                    )}
                  </div>
                )
              ) : null}

              {msgProgramadaParams?.length > 0 &&
                analiseArquivo.amostras.length > 0 && (
                  <div>
                    <p>Amostras de mensagens que serão enviadas:</p>

                    {analiseArquivo?.amostras?.map((amostra, idx) => {
                      if (!amostra) return null;
                      const msg = msgProgramada?.conteudo?.replace(
                        /{{[^}]+}}/g,
                        (match: any) => {
                          const param = match.replace(/[{}]/g, '');
                          if (associacoes[param]?.tipo === 'fixo')
                            return associacoes[param]?.valor;
                          return amostra?.[associacoes[param]?.valor];
                        },
                      );

                      return (
                        <div
                          key={idx}
                          style={{
                            fontSize: '1.2rem',
                            color: '#103668',
                            textAlign: 'center',
                            padding: '0.5rem 1rem',
                            backgroundColor: '#0001',
                            margin: '0.25rem',
                          }}
                        >
                          {msg}
                        </div>
                      );
                    })}
                  </div>
                )}
            </VBox>
          </GridItem>
        </Grid>
      )}

      {step == 3 && (
        <div>
          <Subtitulo>Revisão</Subtitulo>

          {tipoDisparo === 'simples' && (
            <>
              <p style={{ width: '100%', textAlign: 'center' }}>
                As notificações ocorrerão por este canal:
              </p>

              <div
                style={{
                  fontSize: '1.2rem',
                  color: '#103668',
                  textAlign: 'center',
                  padding: '1rem',
                }}
              >
                <p data-testid="preview-nome-canal">{msgProgramada?.canal?.nome}</p>
              </div>

              <p style={{ width: '100%', textAlign: 'center' }}>
                A notificação tem o seguinte padrão:
              </p>

              <div
                style={{
                  fontSize: '1.2rem',
                  color: '#103668',
                  textAlign: 'center',
                  padding: '1rem',
                  border: '1px solid #103668',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--tc-color-gray-100)',
                }}
                data-testid="preview-mensagem"
              >
                <div>
                  {renderHeaderPreview(msgProgramada)}
                  <div>{msgProgramada?.conteudo}</div>
                </div>
              </div>
            </>
          )}

          {tipoDisparo === 'avancado' && (
            <>
              <p style={{ width: '100%', textAlign: 'center' }}>
                O fluxo de campanha selecionado será disparado para cada destinatário:
              </p>
              <div
                style={{
                  fontSize: '1.2rem',
                  color: '#103668',
                  textAlign: 'center',
                  padding: '1rem',
                  border: '1px solid #103668',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--tc-color-gray-100)',
                }}
              >
                {fluxosCampanhaOptions.find((f: any) => f.id?.toString() === dialogoDisparoId?.toString())?.label ?? dialogoDisparoId}
              </div>
            </>
          )}

          <p style={{ width: '100%', textAlign: 'center' }}>
            Quantidade de contatos que receberão esta notificação:
          </p>

          <div
            style={{
              fontSize: '1.2rem',
              color: '#103668',
              textAlign: 'center',
              padding: '1rem',
            }}
            data-testid="preview-qtd-contatos"
          >
            {origem === 'upload' && analiseArquivo.qtdContatos}
            {origem === 'cliente' && filter?.cliente_ids?.length}
            {origem === 'cliente_grupo' && filter?.grupo_ids?.length}
          </div>

          {tipoDisparo === 'simples' && (
            <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #eee', borderRadius: '5px' }}>
              <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
                Quando o cliente responder esta mensagem:
              </label>
              <VBox>
                <CampoRadio
                  id="enc-padrao"
                  name="tipoEncaminhamento"
                  label="Encaminhar para o fluxo padrão do canal"
                  checked={tipoEncaminhamento === 'padrao'}
                  onChange={() => setTipoEncaminhamento('padrao')}
                />
                <CampoRadio
                  id="enc-grupo"
                  name="tipoEncaminhamento"
                  label="Encaminhar para um grupo de atendimento específico"
                  checked={tipoEncaminhamento === 'grupo'}
                  onChange={() => setTipoEncaminhamento('grupo')}
                />
                {tipoEncaminhamento === 'grupo' && (
                  <div style={{ marginLeft: '28px', marginTop: '6px' }}>
                    <CampoSeletor
                      label="Grupo para encaminhamento"
                      value={grupoEncaminhamentoId ?? ''}
                      onChange={setGrupoEncaminhamentoId}
                      options={gruposEncaminhamentoOptions}
                      placeholder="Selecione o grupo..."
                      required
                    />
                  </div>
                )}
              </VBox>
            </div>
          )}

          <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #eee', borderRadius: '5px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>
              Quando a campanha deve iniciar?
            </label>
            <VBox>
              <CampoRadio
                id="inicio-imediato"
                name="tipoInicio"
                label="Enviar agora"
                checked={tipoInicio === 'imediato'}
                onChange={() => setTipoInicio('imediato')}
              />
              <CampoRadio
                id="inicio-agendado"
                name="tipoInicio"
                label="Agendar para uma data e horário específicos"
                checked={tipoInicio === 'agendado'}
                onChange={() => setTipoInicio('agendado')}
              />
              {tipoInicio === 'agendado' && (
                <div style={{ marginLeft: '28px', marginTop: '6px', maxWidth: '320px' }}>
                  <CampoData
                    instanceId="data_agendamento"
                    type="datetime-local"
                    value={dataAgendamento}
                    onChange={setDataAgendamento}
                    required
                  />
                  {dataAgendamento && new Date(dataAgendamento) <= new Date() && (
                    <AlertBox variant="info" style={{ marginTop: '8px' }}>
                      Escolha uma data e horário no futuro.
                    </AlertBox>
                  )}
                </div>
              )}
            </VBox>
          </div>

          <p style={{ width: '100%', textAlign: 'center' }}>
            {tipoInicio === 'agendado'
              ? 'Ao clicar em Próximo, a campanha será agendada e o envio começará automaticamente na data e horário informados. Você poderá acompanhar o estado dos envios na tela de campanhas.'
              : 'Ao clicar em Próximo, o envio das notificações terá início. Você poderá acompanhar o estado dos envios na tela de campanhas.'}
          </p>
        </div>
      )}

      {step === 4 && (
        <div>
          <Subtitulo>Execução</Subtitulo>

          <p style={{ width: '100%', textAlign: 'center' }}>
            {tipoInicio === 'agendado'
              ? `A campanha foi agendada para ${dataAgendamento ? new Date(dataAgendamento).toLocaleString('pt-BR') : ''}.`
              : 'As notificações estão sendo enviadas neste momento.'}
          </p>
          <p style={{ width: '100%', textAlign: 'center' }}>
            Acompanhe o progresso na tela de campanhas.
          </p>

          {analiseArquivo?.amostras?.map((amostra, idx) => {
            if (!amostra) return null;
            const msg = msgProgramada?.conteudo?.replace(
              /{{[^}]+}}/g,
              (match: any) => {
                const param = match.replace(/[{}]/g, '');
                if (associacoes[param]?.tipo === 'fixo')
                  return associacoes[param]?.valor;
                return amostra?.[associacoes[param]?.valor];
              },
            );

            return (
              <div
                key={idx}
                style={{
                  color: '#103668',
                  textAlign: 'center',
                  backgroundColor: '#0001',
                  margin: '0.25rem',
                }}
              >
                {msg}
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        {!hideControls && (
          <Botao
            variant="secondary"
            type="button"
            disabled={!canGoPreviousStep || step <= 0 || step >= 4}
            onClick={() => setStep(step - 1)}
            style={{ visibility: step <= 0 || step >= 4 ? 'hidden' : null }}
            icon={mdiArrowLeft}
          >
            Anterior
          </Botao>
        )}

        {!hideControls && step < 3 && (
          <Botao
            type="button"
            disabled={!canGoNextStep || step >= 3}
            onClick={() => setStep(step + 1)}
            icon={mdiArrowRight}
          >
            Próximo
          </Botao>
        )}

        {!hideControls && step === 3 && (
          <Botao
            type="button"
            disabled={!canGoNextStep}
            onClick={() => handleExecute()}
            icon={mdiArrowRight}
          >
            Executar
          </Botao>
        )}
      </div>
    </>
  );
};
